import { ChatProps, Player } from '@anm/hosting-player';
import { useEffect, RefObject } from 'react';

type HookProps = {
  player: Player | null;
  currentVideo: RefObject<HTMLDivElement>;
  chatProps?: ChatProps;
  isChatMobileView?: boolean;
};

const useChatControls = ({ player, currentVideo, chatProps, isChatMobileView }: HookProps) => {
  const canShowChatButtons = chatProps?.onToggleInput && chatProps?.onToggleChat;
  const panel = currentVideo.current?.querySelector('.plyr__controls');
  const chatWrapper = document.querySelector('.webinar_chat') as HTMLDivElement;

  const movePanel = (canMove: boolean) => {
    if (!chatWrapper) return;

    chatWrapper.style.transform = canMove ? 'translateY(38px)' : 'none';
  };

  useEffect(() => {
    const container = player?.elements.container;
    if (!container || !isChatMobileView || !chatWrapper) return;

    const observer = new MutationObserver(e => {
      const target = e[0].target as HTMLElement;

      movePanel(target.classList.contains('plyr--hide-controls') && !chatProps?.isInputOpened);
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['class'],
      childList: false,
      characterData: false
    });

    return () => observer.disconnect();
  }, [player, chatProps?.isInputOpened, isChatMobileView, chatWrapper]);

  useEffect(() => {
    if (!panel) return;
    const isHidden = panel?.classList.contains('disabled');

    isHidden && !chatProps?.isInputOpened && panel?.classList.remove('disabled');
  }, [chatProps?.isInputOpened, panel]);

  useEffect(() => {
    const fs = currentVideo.current?.querySelector('[data-plyr=fullscreen]');
    const isAlreadyExists = currentVideo.current?.querySelector('.live-btn');

    if (!panel || isAlreadyExists || !canShowChatButtons || !player || !fs || !chatProps?.isChat || !isChatMobileView) {
      return;
    }

    const chatBtn = document.createElement('div');
    chatBtn.classList.add('chat_btn');
    chatProps.isChatOpened && chatBtn.classList.add('active');

    const inputBtn = document.createElement('div');
    inputBtn.classList.add('chat_input_btn');

    const handleToggleChat = () => {
      chatBtn.classList.toggle('active');
      chatProps?.onToggleChat();

      if (chatBtn.classList.contains('active')) {
        inputBtn.classList.add('visible');
      } else inputBtn.classList.remove('visible');
    };

    const handleToggleInput = () => {
      chatProps.onToggleInput();
      panel.classList.toggle('disabled');
    };

    chatBtn.addEventListener('click', handleToggleChat);
    inputBtn.addEventListener('click', handleToggleInput);
    chatProps.isChatOpened && inputBtn.classList.add('visible');

    panel.insertBefore(inputBtn, fs);
    panel.insertBefore(chatBtn, fs);

    return () => {
      chatBtn.removeEventListener('click', handleToggleChat);
      inputBtn.removeEventListener('click', handleToggleInput);
    };
  }, [currentVideo.current, canShowChatButtons, player, chatProps?.isChat, panel, isChatMobileView]);
};

export default useChatControls;
